/**
 * The marketing calendar's shared vocabulary.
 *
 * Ported from the standalone AFMG Marketing Calendar that Altitude has been
 * using as a single HTML file. The campaign types, the status ladder and the
 * seasonal month themes are kept exactly as they were, because staff and
 * clients already read those colours and labels - changing them during the
 * move into the portal would make the familiar tool look like a new one.
 *
 * WHY THIS LIVES IN shared/ AND NOT IN THE COMPONENT
 * --------------------------------------------------
 * The server validates these values on write and the browser paints them on
 * read. Two copies of the list is how a type gets added to the dropdown and
 * then rejected by the API. The lists here are the same arrays the Drizzle
 * enums are built from, and a test asserts they have not drifted apart.
 *
 * WHY DAYS ARE STRINGS
 * --------------------
 * Every date in this module is a calendar day, "YYYY-MM-DD", never an instant.
 * A post planned for the Fourth of July is on the Fourth for everyone looking
 * at it. Day strings in this format also compare correctly with < and >, so a
 * campaign's span is tested without constructing a single Date.
 */

export const CAMPAIGN_TYPES = ["email", "social", "promo", "event", "challenge", "content", "ad"] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

export const CAMPAIGN_STATUSES = ["idea", "planned", "in_progress", "live", "done", "on_hold"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export interface CampaignTypeStyle {
  type: CampaignType;
  label: string;
  icon: string;
  color: string;
}

/** Colours carried over from the standalone calendar. */
export const CAMPAIGN_TYPE_STYLES: readonly CampaignTypeStyle[] = [
  { type: "email", label: "Email", icon: "✉", color: "#3b82f6" },
  { type: "social", label: "Social", icon: "◆", color: "#8b5cf6" },
  { type: "promo", label: "Promo", icon: "％", color: "#f97316" },
  { type: "event", label: "Event", icon: "★", color: "#0ea5e9" },
  { type: "challenge", label: "Challenge", icon: "▲", color: "#84cc16" },
  { type: "content", label: "Content", icon: "✎", color: "#14b8a6" },
  { type: "ad", label: "Ad", icon: "❖", color: "#ec4899" },
];

export interface CampaignStatusStyle {
  status: CampaignStatus;
  label: string;
  color: string;
  /** Share of the way to done, for the completion bar. Idea is not 0% because
   *  a logged idea is further along than nothing at all. */
  weight: number;
}

export const CAMPAIGN_STATUS_STYLES: readonly CampaignStatusStyle[] = [
  { status: "idea", label: "Idea", color: "#94a3b8", weight: 0.1 },
  { status: "planned", label: "Planned", color: "#3b82f6", weight: 0.35 },
  { status: "in_progress", label: "In progress", color: "#f59e0b", weight: 0.65 },
  { status: "live", label: "Live", color: "#84cc16", weight: 0.9 },
  { status: "done", label: "Done", color: "#10b981", weight: 1 },
  { status: "on_hold", label: "On hold", color: "#ef4444", weight: 0 },
];

export const typeStyle = (type: string): CampaignTypeStyle =>
  CAMPAIGN_TYPE_STYLES.find(style => style.type === type) ?? CAMPAIGN_TYPE_STYLES[1];

export const statusStyle = (status: string): CampaignStatusStyle =>
  CAMPAIGN_STATUS_STYLES.find(style => style.status === status) ?? CAMPAIGN_STATUS_STYLES[0];

/** Seasonal headers, kept from the original calendar. */
export const MONTH_THEMES: readonly { month: string; tag: string; gradient: [string, string] }[] = [
  { month: "January", tag: "New Year, New Goals", gradient: ["#0e7c86", "#14b8a6"] },
  { month: "February", tag: "Heart Health Month", gradient: ["#ec4899", "#f97316"] },
  { month: "March", tag: "Spring Into Shape", gradient: ["#84cc16", "#14b8a6"] },
  { month: "April", tag: "Fresh Start Fitness", gradient: ["#0ea5e9", "#84cc16"] },
  { month: "May", tag: "Strong Month", gradient: ["#f59e0b", "#ef4444"] },
  { month: "June", tag: "Summer Shred", gradient: ["#f97316", "#f59e0b"] },
  { month: "July", tag: "Peak Season Push", gradient: ["#0ea5e9", "#0e7c86"] },
  { month: "August", tag: "Finish Strong", gradient: ["#ef4444", "#f97316"] },
  { month: "September", tag: "Back-to-Routine", gradient: ["#f59e0b", "#14b8a6"] },
  { month: "October", tag: "Fall Fitness Challenge", gradient: ["#f97316", "#8b5cf6"] },
  { month: "November", tag: "Gratitude & Grind", gradient: ["#8b5cf6", "#0e7c86"] },
  { month: "December", tag: "Holiday Wellness", gradient: ["#0ea5e9", "#14b8a6"] },
];

/* -------------------------------------------------------------------------- */
/* Day arithmetic, on strings                                                 */
/* -------------------------------------------------------------------------- */

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isDay = (value: string): boolean => DAY_PATTERN.test(value);

export const dayString = (year: number, monthIndex: number, day: number): string =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

/** First and last day of a month, inclusive, as day strings. */
export function monthRange(year: number, monthIndex: number): { first: string; last: string } {
  // Day 0 of the following month is the last day of this one, which handles
  // February and leap years without a table of month lengths.
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return { first: dayString(year, monthIndex, 1), last: dayString(year, monthIndex, lastDay) };
}

export interface CampaignSpan {
  startsOn: string;
  endsOn?: string | null;
}

/**
 * Does a campaign cover this day?
 *
 * A campaign with no end date is a single-day post, not an open-ended one.
 * Treating a missing end as "forever" would smear one forgotten entry across
 * every month the client ever opens.
 */
export function coversDay(campaign: CampaignSpan, day: string): boolean {
  const end = campaign.endsOn || campaign.startsOn;
  return day >= campaign.startsOn && day <= end;
}

export function overlapsRange(campaign: CampaignSpan, first: string, last: string): boolean {
  const end = campaign.endsOn || campaign.startsOn;
  return campaign.startsOn <= last && end >= first;
}

export interface CalendarCell<T extends CampaignSpan> {
  /** Null for the leading and trailing blanks that pad the grid to whole weeks. */
  day: string | null;
  dayOfMonth: number | null;
  campaigns: T[];
}

/**
 * Lay a month out as weeks of seven cells, Sunday first.
 *
 * Returns only as many weeks as the month actually occupies. A fixed six-row
 * grid leaves an empty strip under most months, which is what made the
 * standalone calendar look broken in February.
 */
export function monthGrid<T extends CampaignSpan>(
  year: number,
  monthIndex: number,
  campaigns: T[]
): CalendarCell<T>[][] {
  // UTC throughout: a local-time Date for the first of the month can resolve
  // to the previous day west of Greenwich and shift the whole grid by one.
  const firstWeekday = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
  const dayCount = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

  const cells: CalendarCell<T>[] = [];
  for (let blank = 0; blank < firstWeekday; blank++) {
    cells.push({ day: null, dayOfMonth: null, campaigns: [] });
  }
  for (let day = 1; day <= dayCount; day++) {
    const iso = dayString(year, monthIndex, day);
    cells.push({
      day: iso,
      dayOfMonth: day,
      campaigns: campaigns.filter(campaign => coversDay(campaign, iso)),
    });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ day: null, dayOfMonth: null, campaigns: [] });
  }

  const weeks: CalendarCell<T>[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }
  return weeks;
}

export interface CalendarTotals {
  total: number;
  live: number;
  done: number;
  awaiting: number;
  /** Weighted completion across everything in view, 0-100. */
  completionPercent: number;
  byType: { type: CampaignType; label: string; color: string; count: number }[];
}

/**
 * Headline figures for the month in view.
 *
 * Completion is weighted by status rather than counted as done-over-total,
 * because a month of twelve planned posts and no published ones is not 0%
 * complete and reporting it that way makes the number useless as a signal.
 */
export function calendarTotals(campaigns: { type: string; status: string }[]): CalendarTotals {
  const weight = campaigns.reduce((sum, campaign) => sum + statusStyle(campaign.status).weight, 0);

  return {
    total: campaigns.length,
    live: campaigns.filter(campaign => campaign.status === "live").length,
    done: campaigns.filter(campaign => campaign.status === "done").length,
    awaiting: campaigns.filter(campaign => campaign.status === "idea" || campaign.status === "planned").length,
    completionPercent: campaigns.length === 0 ? 0 : Math.round((weight / campaigns.length) * 100),
    byType: CAMPAIGN_TYPE_STYLES.map(style => ({
      type: style.type,
      label: style.label,
      color: style.color,
      count: campaigns.filter(campaign => campaign.type === style.type).length,
    })).filter(entry => entry.count > 0),
  };
}
